import { sanitizeNullStrings } from "@/utils/sanitize";
import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Divider,
	FormControlLabel,
	Grid2,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { Operator } from "../models/Operator";

interface FormErrors {
	Name?: string;
}

interface OperatorDetailViewerProps {
	operator: Operator | null;
	existingOperators: Operator[];
	isSaving: boolean;
	onSave: (operator: Operator) => void;
	onClose: () => void;
}

export const OperatorDetailViewer = ({
	operator,
	existingOperators,
	isSaving,
	onSave,
	onClose,
}: OperatorDetailViewerProps) => {
	const [formData, setFormData] = useState<Operator | null>(null);
	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	const isNewOperator = !operator?.Id;

	const validateForm = useMemo(() => {
		return (data: Operator | null): FormErrors => {
			const newErrors: FormErrors = {};

			if (!data?.Name || data.Name.trim().length === 0) {
				newErrors.Name = "Name is required";
			} else {
				const isDuplicate = existingOperators.some(
					(o) =>
						o.Id !== data.Id &&
						o.Name.toLowerCase() === data.Name.trim().toLowerCase(),
				);
				if (isDuplicate) {
					newErrors.Name = "This name already exists";
				}
			}

			return newErrors;
		};
	}, [existingOperators]);

	const formErrors = useMemo(
		() => validateForm(formData),
		[formData, validateForm],
	);
	const isFormValid = Object.keys(formErrors).length === 0;
	const canCreate = isNewOperator && isFormValid;

	useEffect(() => {
		if (operator) {
			setFormData(sanitizeNullStrings({ ...operator }));
		} else {
			setFormData(null);
		}
		setErrors({});
		setTouched({});
	}, [operator]);

	const handleChange = (field: keyof Operator, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
	};

	const handleBlur = (field: keyof Operator) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		if (formData) {
			setErrors(validateForm(formData));
		}
	};

	const handleBlurSave = (field: keyof Operator, value: unknown) => {
		handleBlur(field);

		if (isNewOperator) return;

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		if (currentErrors[field as keyof FormErrors]) return;

		const originalValue = operator?.[field];
		const normalizedOriginal = originalValue === null ? "" : originalValue;
		const normalizedNew = value === null ? "" : value;

		if (normalizedOriginal !== normalizedNew) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleCheckboxChange = (field: keyof Operator, value: unknown) => {
		handleChange(field, value);

		if (isNewOperator) return;

		if (operator?.[field] !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleCreate = () => {
		setTouched({ Name: true });
		setErrors(formErrors);

		if (formData && canCreate) {
			onSave(formData);
		}
	};

	if (!operator) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select an operator to view details</Typography>
			</Box>
		);
	}

	if (!formData) return null;

	return (
		<Box sx={{ p: 1.5, height: "100%", overflow: "auto" }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
						{isNewOperator ? "New Operator" : formData.Name}
					</Typography>
					{isSaving && <CircularProgress size={16} />}
				</Box>
				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					{isNewOperator && (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={!canCreate || isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					)}
					<Tooltip title="Close">
						<IconButton size="small" onClick={onClose} disabled={isSaving}>
							<CloseIcon />
						</IconButton>
					</Tooltip>
				</Box>
			</Box>

			<Divider sx={{ mb: 1.5 }} />

			<Grid2 container spacing={1.5}>
				<Grid2 size={{ xs: 12 }}>
					<Typography
						sx={{
							color: "primary.main",
							fontWeight: 600,
							fontSize: "0.85rem",
							mb: 0.5,
						}}
					>
						Basic Information
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Name"
						value={formData.Name}
						onChange={(e) => handleChange("Name", e.target.value)}
						onBlur={(e) => handleBlurSave("Name", e.target.value)}
						fullWidth
						size="small"
						required
						error={touched.Name && !!errors.Name}
						helperText={touched.Name && errors.Name}
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Clock Number"
						value={formData.ClockNumber ?? ""}
						onChange={(e) => handleChange("ClockNumber", e.target.value)}
						onBlur={(e) => handleBlurSave("ClockNumber", e.target.value)}
						fullWidth
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={formData.Active}
								onChange={(e) =>
									handleCheckboxChange("Active", e.target.checked)
								}
								size="small"
							/>
						}
						label="Active"
						sx={{ ml: 0 }}
					/>
				</Grid2>
			</Grid2>
		</Box>
	);
};
